import { createContext, Dispatch, ReactNode, useContext, useEffect, useReducer, useRef } from 'react'
import {
  MerchantApplicationAction,
  merchantApplicationReducer,
  getInitialState,
} from './merchantApplicationReducer'
import { MerchantApplicationState } from './merchantApplicationModel'
import { useMerchantApplicationPreFillQuery } from './merchantApplicationQueries'

type MerchantApplicationContextData = {
  state: MerchantApplicationState
  dispatch: Dispatch<MerchantApplicationAction>
  preFillError: boolean
}

const MerchantApplicationContext = createContext<MerchantApplicationContextData | undefined>(undefined)

type Props = {
  children: ReactNode
}

const MerchantApplicationProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(merchantApplicationReducer, getInitialState())
  const hasInitialized = useRef(false)
  const preFillQuery = useMerchantApplicationPreFillQuery()

  useEffect(() => {
    if (preFillQuery.data && !hasInitialized.current) {
      hasInitialized.current = true
      dispatch({ type: 'INIT_FROM_PREFILL', payload: preFillQuery.data })
    }
  }, [preFillQuery.data])

  useEffect(() => {
    if (preFillQuery.isError) {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [preFillQuery.isError])

  return (
    <MerchantApplicationContext.Provider value={{ state, dispatch, preFillError: preFillQuery.isError }}>
      {children}
    </MerchantApplicationContext.Provider>
  )
}

export default MerchantApplicationProvider

export const useMerchantApplication = () => {
  const context = useContext(MerchantApplicationContext)
  if (!context) {
    throw new Error('useMerchantApplication must be used within MerchantApplicationProvider')
  }
  return context
}
